import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { setupInterceptorsTo } from '@/shared/services/httpInterceptors';
import type { Area, Location, Zone } from '../models/WarehouseModels';
import type { RefTag } from '../models/RefTagModels';
import type { RefArticle } from '../models/RefArticleModels';
import type {
  TerminalArticleContext,
  TerminalInventoryObjectContext,
  TerminalLocationContext,
  TerminalMoveRequest,
  TerminalReceiveRequest,
  TerminalResolveRequest,
  TerminalResolveResponse,
  TerminalIssueRequest,
  TerminalIssueHandlingUnitRequest,
  TerminalMoveHandlingUnitRequest,
  TerminalArticleSearchItem,
  TerminalArticleSearchQuery,
  TerminalArticleSearchResponse,
  TerminalReusableHandlingUnitLookupResponse,
  WarehouseTaskDto,
  CreateCountTaskRequest,
  CountObservationRequest,
  CompleteCountLocationRequest,
  CompleteCountArticleScopeRequest,
  CountTaskPreviewDto,
} from '../models/TerminalModels';
import { trimBarcodeForRequest } from '../utils/barcodeNormalizer';

import config from '../../../../appsettings.dev.json';

const env = window.__ENV__ ?? null;

const BASE_URL = (env != undefined && env?.WMS_BASE_URL != undefined && env?.WMS_BASE_URL != '' ? env?.WMS_BASE_URL : config.wms.baseUrl) + '/v1';

const tenantFromUrl = window.location.hostname.split('.')[0];
const tenant =
  tenantFromUrl !== undefined && tenantFromUrl !== 'www' && tenantFromUrl !== 'localhost' && tenantFromUrl !== '' && tenantFromUrl != 'zelos'
    ? tenantFromUrl
    : env != undefined && env?.AUTH_DEFAULT_REALM != undefined && env?.AUTH_DEFAULT_REALM != ''
      ? env?.AUTH_DEFAULT_REALM
      : config.auth.defaultRealm;

const apiClient = setupInterceptorsTo(
  axios.create({
    baseURL: BASE_URL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      tenant,
    },
  })
);

function buildQuery(paramsData: Record<string, string | number | boolean | null | undefined>) {
  const params = new URLSearchParams();

  Object.entries(paramsData).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function trimNullableBarcode(value: string | null | undefined) {
  if (value == null) return value;
  return trimBarcodeForRequest(value);
}

function trimNullableBarcodeOrNull(value: string | null | undefined) {
  const trimmed = trimNullableBarcode(value);
  return trimmed === '' ? null : trimmed;
}

function trimReceiveRequest(data: TerminalReceiveRequest): TerminalReceiveRequest {
  return {
    ...data,
    targetAreaCode: trimNullableBarcodeOrNull(data.targetAreaCode),
    targetZoneCode: trimNullableBarcodeOrNull(data.targetZoneCode),
    targetLocationCode: trimNullableBarcodeOrNull(data.targetLocationCode),
    skuOrIdentifier: trimNullableBarcodeOrNull(data.skuOrIdentifier),
    existingHandlingUnitCode: trimNullableBarcodeOrNull(data.existingHandlingUnitCode),
    newHandlingUnit: data.newHandlingUnit
      ? {
          ...data.newHandlingUnit,
          code: trimBarcodeForRequest(data.newHandlingUnit.code),
        }
      : data.newHandlingUnit,
  };
}

function trimIssueRequest(data: TerminalIssueRequest): TerminalIssueRequest {
  return {
    ...data,
    sourceAreaCode: trimNullableBarcodeOrNull(data.sourceAreaCode),
    sourceZoneCode: trimNullableBarcodeOrNull(data.sourceZoneCode),
    sourceLocationCode: trimNullableBarcodeOrNull(data.sourceLocationCode),
    sourceHandlingUnitCode: trimNullableBarcodeOrNull(data.sourceHandlingUnitCode),
    skuOrIdentifier: trimNullableBarcodeOrNull(data.skuOrIdentifier),
  };
}

function trimMoveHandlingUnitRequest(data: TerminalMoveHandlingUnitRequest): TerminalMoveHandlingUnitRequest {
  return {
    ...data,
    locationCode: trimNullableBarcodeOrNull(data.locationCode),
  };
}

function trimMoveRequest(data: TerminalMoveRequest): TerminalMoveRequest {
  return {
    ...data,
    sourceAreaCode: trimNullableBarcodeOrNull(data.sourceAreaCode),
    sourceZoneCode: trimNullableBarcodeOrNull(data.sourceZoneCode),
    sourceLocationCode: trimNullableBarcodeOrNull(data.sourceLocationCode),
    sourceHandlingUnitCode: trimNullableBarcodeOrNull(data.sourceHandlingUnitCode),
    skuOrIdentifier: trimNullableBarcodeOrNull(data.skuOrIdentifier),
    targetWarehouseCode: trimNullableBarcodeOrNull(data.targetWarehouseCode),
    targetAreaCode: trimNullableBarcodeOrNull(data.targetAreaCode),
    targetZoneCode: trimNullableBarcodeOrNull(data.targetZoneCode),
    targetLocationCode: trimNullableBarcodeOrNull(data.targetLocationCode),
    targetHandlingUnitCode: trimNullableBarcodeOrNull(data.targetHandlingUnitCode),
    existingHandlingUnitCode: trimNullableBarcodeOrNull(data.existingHandlingUnitCode),
    newHandlingUnit: data.newHandlingUnit
      ? {
          ...data.newHandlingUnit,
          code: trimBarcodeForRequest(data.newHandlingUnit.code),
        }
      : data.newHandlingUnit,
  };
}

export default {
  //#region Warehouse
  getAllWarehouses(): Promise<AxiosResponse> {
    return apiClient.get('/warehouses');
  },

  getAreasByWarehouse(warehouseId: number): Promise<AxiosResponse<Area[]>> {
    return apiClient.get(`/warehouse/${warehouseId}/areas`);
  },

  getZonesByArea(areaId: number): Promise<AxiosResponse<Zone[]>> {
    return apiClient.get(`/area/${areaId}/zones`);
  },

  getLocations(params: { warehouseId: number; areaId?: number | null; zoneId?: number | null; occupied?: boolean | null }): Promise<AxiosResponse<Location[]>> {
    return apiClient.get(
      `/warehouse/${params.warehouseId}/locations${buildQuery({ areaId: params.areaId, zoneId: params.zoneId, occupied: params.occupied })}`
    );
  },
  //#endregion

  resolveScan(data: TerminalResolveRequest): Promise<AxiosResponse<TerminalResolveResponse>> {
    return apiClient.post('/scan/resolve', {
      ...data,
      rawCode: trimBarcodeForRequest(data.rawCode),
    });
  },

  getLocationContext(warehouseId: number, codeOrBarcode: string): Promise<AxiosResponse<TerminalLocationContext>> {
    return apiClient.get(`/terminal/location-context${buildQuery({ warehouseId, codeOrBarcode: trimBarcodeForRequest(codeOrBarcode) })}`);
  },

  getRefArticleById(id: string): Promise<AxiosResponse<RefArticle>> {
    return apiClient.get(`/ref/article/${encodeURIComponent(id)}`);
  },

  getArticleContext(warehouseId: number, skuOrIdentifier: string): Promise<AxiosResponse<TerminalArticleContext>> {
    return apiClient.get(`/terminal/article-context${buildQuery({ warehouseId, skuOrIdentifier: trimBarcodeForRequest(skuOrIdentifier) })}`);
  },

  searchArticles(query: TerminalArticleSearchQuery): Promise<AxiosResponse<TerminalArticleSearchItem[] | TerminalArticleSearchResponse>> {
    return apiClient.get(`/ref/articles${buildQuery({ search: query.search, limit: query.limit, TagId: query.tagId })}`);
  },

  getAllTags(): Promise<AxiosResponse<RefTag[]>> {
    return apiClient.get('/ref/tags');
  },

  getInventoryObjectContext(warehouseId: number, code: string): Promise<AxiosResponse<TerminalInventoryObjectContext>> {
    return apiClient.get(`/terminal/inventory-object-context${buildQuery({ warehouseId, code: trimBarcodeForRequest(code) })}`);
  },

  getReusableHandlingUnit(sourceWarehouseId: number, handlingUnitCode: string): Promise<AxiosResponse<TerminalReusableHandlingUnitLookupResponse>> {
    return apiClient.get(`/terminal/reusable-handling-unit${buildQuery({ sourceWarehouseId, handlingUnitCode: trimBarcodeForRequest(handlingUnitCode) })}`);
  },

  receiveStock(data: TerminalReceiveRequest): Promise<AxiosResponse> {
    return apiClient.post('/terminal/stock/receive', trimReceiveRequest(data));
  },

  moveStock(data: TerminalMoveRequest): Promise<AxiosResponse> {
    return apiClient.post('/terminal/stock/move', trimMoveRequest(data));
  },

  issueStock(data: TerminalIssueRequest): Promise<AxiosResponse> {
    return apiClient.post('/terminal/stock/issue', trimIssueRequest(data));
  },

  issueHandlingUnit(id: number, data: TerminalIssueHandlingUnitRequest): Promise<AxiosResponse> {
    return apiClient.post(`/handling-units/${id}/issue`, data);
  },

  moveHandlingUnit(id: number, data: TerminalMoveHandlingUnitRequest): Promise<AxiosResponse> {
    return apiClient.post(`/handling-units/${id}/move-to-location`, trimMoveHandlingUnitRequest(data));
  },

  getWarehouseTask(id: number): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.get(`/warehouse-tasks/${id}`);
  },

  createCountTask(data: CreateCountTaskRequest): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.post('/warehouse-tasks/count', data);
  },

  claimWarehouseTask(id: number): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.post(`/warehouse-tasks/${id}/claim`, {});
  },

  startWarehouseTask(id: number): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.post(`/warehouse-tasks/${id}/start`, {});
  },

  startCountLocation(taskId: number, locationId: number): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.post(`/warehouse-tasks/${taskId}/count/locations/${locationId}/start`, {});
  },

  upsertCountObservation(taskId: number, locationId: number, data: CountObservationRequest): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.put(`/warehouse-tasks/${taskId}/count/locations/${locationId}/observations`, {
      ...data,
      handlingUnitCode: trimNullableBarcodeOrNull(data.handlingUnitCode),
    });
  },

  completeCountLocation(taskId: number, locationId: number, data: CompleteCountLocationRequest): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.post(`/warehouse-tasks/${taskId}/count/locations/${locationId}/complete`, data);
  },

  startCountArticleScope(taskId: number): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.post(`/warehouse-tasks/${taskId}/count/article-scope/start`, {});
  },

  upsertCountArticleScopeObservation(taskId: number, data: CountObservationRequest): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.put(`/warehouse-tasks/${taskId}/count/article-scope/observations`, {
      ...data,
      handlingUnitCode: trimNullableBarcodeOrNull(data.handlingUnitCode),
    });
  },

  completeCountArticleScope(taskId: number, data: CompleteCountArticleScopeRequest): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.post(`/warehouse-tasks/${taskId}/count/article-scope/complete`, data);
  },

  completeCountTask(taskId: number, completionMode: 'CloseOnly' | 'ApplyAdjustments'): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.post(`/warehouse-tasks/${taskId}/count/complete`, { completionMode });
  },

  getCountPreview(taskId: number): Promise<AxiosResponse<CountTaskPreviewDto>> {
    return apiClient.get(`/warehouse-tasks/${taskId}/count/preview`);
  },

  resumeWarehouseTask(id: number): Promise<AxiosResponse<WarehouseTaskDto>> {
    return apiClient.post(`/warehouse-tasks/${id}/resume`, {});
  },
};
